import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { navItems, site } from "../utils/data";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile sheet is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-200 ${
          scrolled
            ? "border-b border-paper-300 bg-paper/85 backdrop-blur-xl"
            : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-shell items-center justify-between gap-6 px-6 lg:px-10">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="group flex items-baseline gap-2"
          >
            <span className="display text-xl tracking-tight text-ink-950">
              {site.short}
            </span>
            <span className="h-1.5 w-1.5 rounded-full bg-clay transition-transform duration-300 group-hover:scale-125" />
          </button>

          <div className="flex items-center gap-6">
            <nav className="hidden items-center gap-8 md:flex">
              {navItems.map((item) => (
                <button
                  key={item.target}
                  onClick={() => go(item.target)}
                  className="group relative text-body font-medium tracking-tight text-ink-600 transition-colors hover:text-ink-950"
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-clay transition-all duration-200 group-hover:w-full" />
                </button>
              ))}
            </nav>

            <button
              onClick={() => go("contact")}
              className="hidden bg-ink-950 px-5 py-2.5 font-display text-sm font-semibold text-paper transition-colors hover:bg-clay md:block"
            >
              Email me
            </button>

            <button
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              className="grid h-10 w-10 place-items-center border border-paper-400 text-ink-950 transition-colors hover:border-clay md:hidden"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 flex flex-col bg-paper pt-[72px] md:hidden"
          >
            <nav className="flex flex-1 flex-col px-6 pt-8">
              {[...navItems, { label: "Contact", target: "contact" }].map(
                (item) => (
                  <button
                    key={item.target}
                    onClick={() => go(item.target)}
                    className="border-b border-paper-300 py-5 text-left"
                  >
                    <span className="display text-3xl tracking-tight text-ink-950">
                      {item.label}
                    </span>
                  </button>
                ),
              )}
            </nav>

            {/* Primary action, always one tap away in the mobile menu */}
            <div className="px-6 pb-10 pt-2">
              <button
                onClick={() => go("contact")}
                className="w-full bg-ink-950 px-6 py-4 font-display text-lg font-semibold text-paper transition-colors hover:bg-clay"
              >
                Email me
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
